import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, Button, Space } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { ProductAttribute } from '../services/ProductAttributeService';
const { Option } = Select;

interface ProductAttributeFormModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: Partial<ProductAttribute>) => Promise<void>;
  initialValues?: ProductAttribute | null;
  isLoading?: boolean;
}

export const ProductAttributeFormModal: React.FC<ProductAttributeFormModalProps> = ({
  open,
  onCancel,
  onSubmit,
  initialValues,
  isLoading
}) => {
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          ...initialValues,
          status: initialValues.status === 'active',
          values: initialValues.values?.map(v => ({ value: v.value })) || [],
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          status: true,
          purpose_id: 1,
          input_type_id: 1,
        });
      }
    }
  }, [open, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Transform values for API
      const formattedValues = {
        ...values,
        status: values.status ? 'active' : 'inactive',
        // In a real app, you'd map string array to ProductAttributeValue array
        values: values.values?.map((v: { value: string }, index: number) => {
          if (!v || !v.value) return null;
          return {
            code: v.value.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 100),
            value: v.value,
            display_order: index,
            is_default: false,
            status: 'active'
          };
        }).filter(Boolean) || []
      };

      await onSubmit(formattedValues);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={initialValues ? 'Edit Attribute' : 'New Attribute'}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={isLoading}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        className="mt-4"
      >


        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="name"
            label="Attribute Name"
            rules={[{ required: true, message: 'Please enter attribute name' }]}
          >
            <Input placeholder="e.g. Color, Size, Material" />
          </Form.Item>

          <Form.Item
            name="code"
            label="Code"
            rules={[{ required: true, message: 'Please enter attribute code' }]}
          >
            <Input placeholder="e.g. color, item_size" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="purpose_id"
            label="Purpose"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value={1}>Specification (e.g. Dimensions)</Option>
              <Option value={2}>Quotation (e.g. Installation required)</Option>
              <Option value={3}>Both</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="input_type_id"
            label="Input Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value={1}>Dropdown / Select</Option>
              <Option value={2}>Number</Option>
              <Option value={3}>Text</Option>
              <Option value={4}>Boolean (Yes/No)</Option>
              <Option value={5}>Date</Option>
              <Option value={6}>Date & Time</Option>
            </Select>
          </Form.Item>
        </div>

        <div className="mb-4">
          <Form.Item
            name="status"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </div>

        <Form.List name="values">
          {(fields, { add, remove }) => (
            <div className="mt-4 border rounded-lg p-4 bg-gray-50">
              <div className="mb-2 font-medium">Preset Values (Optional)</div>
              {fields.map(({ key, ...restField }) => (
                <div key={key} className="mb-2">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item
                      {...restField}
                      name={[restField.name, 'value']}
                      noStyle
                    >
                      <Input placeholder="Value (e.g. Red, Blue, Small)" />
                    </Form.Item>
                    <Button 
                      type="text" 
                      danger 
                      icon={<MinusCircleOutlined />} 
                      onClick={() => remove(restField.name)}
                    />
                  </Space.Compact>
                </div>
              ))}
              <Form.Item className="mb-0 mt-2">
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Value
                </Button>
              </Form.Item>
            </div>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};
